import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getCategories, getProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { formatINR } from "@/lib/format";
import { SlidersHorizontal, ChevronDown, ChevronUp, RotateCcw } from "lucide-react";

const PRICE_BUCKETS = [
  { label: "All prices", min: null, max: null },
  { label: "Under ₹1,500", min: null, max: 1500 },
  { label: "₹1,500 – ₹2,500", min: 1500, max: 2500 },
  { label: "₹2,500 – ₹3,500", min: 2500, max: 3500 },
  { label: "₹3,500 & above", min: 3500, max: null },
];

const OCCASIONS = [
  { label: "All Occasions", val: "all" },
  { label: "Congratulations", val: "congratulations" },
  { label: "Birthday", val: "birthday" },
  { label: "Anniversary", val: "anniversary" },
  { label: "Wedding", val: "wedding" },
  { label: "Rakhi & Festivals", val: "festival" },
  { label: "Valentine's & Romance", val: "romance" },
  { label: "Graduation", val: "graduation" },
  { label: "Housewarming", val: "housewarming" },
  { label: "Get Well Soon", val: "get-well" },
  { label: "Thank You", val: "thank-you" },
];

export default function Shop() {
  const { category } = useParams();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [priceIdx, setPriceIdx] = useState(0);
  const [selectedOccasion, setSelectedOccasion] = useState("all");
  const [showMobileFilters, setShowMobileFilters] = useState(true);

  useEffect(() => { getCategories().then(setCategories); }, []);

  useEffect(() => {
    setLoading(true);
    const params = category ? { category } : {};
    getProducts(params).then((d) => { setProducts(d); setLoading(false); }).catch(() => setLoading(false));
  }, [category]);

  const currentCat = categories.find((c) => c.slug === category);
  const bucket = PRICE_BUCKETS[priceIdx];

  const activeFiltersCount = (category ? 1 : 0) + (selectedOccasion !== "all" ? 1 : 0) + (priceIdx !== 0 ? 1 : 0);

  const resetFilters = () => {
    setPriceIdx(0);
    setSelectedOccasion("all");
  };

  const CATEGORY_ORDER = { toys: 0, flowers: 1, sweets: 2 };

  const getCategoryRank = (cat) => {
    const c = (cat || "").toLowerCase();
    if (c.includes("toy") || c.includes("plush")) return CATEGORY_ORDER.toys;
    if (c.includes("flower") || c.includes("bouquet")) return CATEGORY_ORDER.flowers;
    if (c.includes("chocolate") || c.includes("sweet")) return CATEGORY_ORDER.sweets;
    return 3;
  };

  const filteredSorted = useMemo(() => {
    let arr = products.filter((p) => {
      if (bucket.min != null && p.price < bucket.min) return false;
      if (bucket.max != null && p.price > bucket.max) return false;
      if (selectedOccasion !== "all") {
        const occ = (p.occasion || "").toLowerCase();
        if (selectedOccasion === "congratulations" && !occ.includes("congratulat")) return false;
        if (selectedOccasion === "birthday" && !occ.includes("birth")) return false;
        if (selectedOccasion === "anniversary" && !occ.includes("annivers")) return false;
        if (selectedOccasion === "wedding" && !occ.includes("wed")) return false;
        if (selectedOccasion === "festival" && (!occ.includes("festiv") && !occ.includes("rakhi"))) return false;
        if (selectedOccasion === "romance" && (!occ.includes("valentin") && !occ.includes("romance") && !occ.includes("love"))) return false;
        if (selectedOccasion === "graduation" && !occ.includes("gradua")) return false;
        if (selectedOccasion === "housewarming" && !occ.includes("house")) return false;
        if (selectedOccasion === "get-well" && (!occ.includes("well") && !occ.includes("care"))) return false;
        if (selectedOccasion === "thank-you" && (!occ.includes("thank") && !occ.includes("gift"))) return false;
      }
      return true;
    });
    if (sort === "price-asc") arr = [...arr].sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") arr = [...arr].sort((a, b) => b.price - a.price);
    else if (sort === "top") arr = [...arr].sort((a, b) => b.rating - a.rating);
    else arr = [...arr].sort((a, b) => getCategoryRank(a.category) - getCategoryRank(b.category) || (a.displayOrder || 0) - (b.displayOrder || 0));
    return arr;
  }, [products, sort, bucket, selectedOccasion]);

  return (
    <main data-testid="shop-page" className="pt-24 md:pt-32 pastel-mesh min-h-screen">
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-6 md:py-12">
        
        {/* Header Title & Sorting */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="overline text-[11px] md:text-[12px]">Shop Gifts</p>
            <h1 className="mt-1 font-display font-semibold text-[#1C1924] text-2xl md:text-5xl tracking-tight">
              {currentCat ? currentCat.name : "All Gifts"}
            </h1>
            <p className="mt-1 text-[#4A4652] text-[13px] md:text-[15px]">
              {filteredSorted.length} thoughtful gifts, ready to send.
            </p>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-auto w-full sm:w-auto justify-between sm:justify-start">
            {/* Mobile filter toggle button */}
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="md:hidden flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-[#EEE7FA] text-[13px] font-semibold text-[#1C1924] shadow-sm active:scale-95 transition"
            >
              <SlidersHorizontal size={14} className="text-[#E5497C]" />
              <span>Filters</span>
              {activeFiltersCount > 0 && (
                <span className="w-5 h-5 rounded-full bg-[#E5497C] text-white text-[10px] grid place-items-center font-bold">
                  {activeFiltersCount}
                </span>
              )}
              {showMobileFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {/* Sort dropdown */}
            <select
              data-testid="shop-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-full bg-white border border-[#EEE7FA] px-4 py-2 text-[13px] md:text-[13.5px] font-medium text-[#1C1924] outline-none focus:ring-2 focus:ring-[#F7C7DC] shadow-sm"
            >
              <option value="featured">Featured</option>
              <option value="top">Top rated</option>
              <option value="price-asc">Price: low to high</option>
              <option value="price-desc">Price: high to low</option>
            </select>
          </div>
        </div>

        {/* Filters Section (Collapsible on mobile) */}
        {showMobileFilters && (
          <div className="mt-6 p-3 md:p-0 rounded-2xl bg-white/60 md:bg-transparent border border-[#F7C7DC]/40 md:border-none backdrop-blur-sm space-y-3 transition-all duration-300">
            
            {/* Category Chips */}
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-0.5 whitespace-nowrap scroll-smooth md:flex-wrap">
              <span className="text-[11px] font-bold text-[#8b8790] uppercase tracking-wider md:hidden shrink-0 pr-1">Category:</span>
              <Link
                to="/shop"
                data-testid="shop-chip-all"
                className={`px-3.5 py-1.5 rounded-full text-[12.5px] md:text-[13px] font-semibold transition shrink-0 ${!category ? "bg-[#1C1924] text-white shadow-sm" : "bg-white text-[#1C1924] border border-[#EEE7FA] hover:bg-[#F4F0FF]"}`}
              >
                All
              </Link>
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/shop/${c.slug}`}
                  data-testid={`shop-chip-${c.slug}`}
                  className={`px-3.5 py-1.5 rounded-full text-[12.5px] md:text-[13px] font-semibold transition shrink-0 ${category === c.slug ? "bg-[#1C1924] text-white shadow-sm" : "bg-white text-[#1C1924] border border-[#EEE7FA] hover:bg-[#F4F0FF]"}`}
                >
                  {c.name}
                </Link>
              ))}
            </div>

            {/* Occasions */}
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-0.5 whitespace-nowrap scroll-smooth md:flex-wrap">
              <span className="flex items-center gap-1 text-[11px] font-bold text-[#8b8790] uppercase tracking-wider shrink-0 pr-1">
                <SlidersHorizontal size={12} className="text-[#E5497C]" /> Occasion:
              </span>
              {OCCASIONS.map((occ) => (
                <button
                  key={occ.val}
                  data-testid={`shop-occasion-${occ.val}`}
                  onClick={() => setSelectedOccasion(occ.val)}
                  className={`px-3 py-1.5 rounded-full text-[12px] md:text-[12.5px] font-semibold transition shrink-0 ${selectedOccasion === occ.val ? "bg-[#1C1924] text-white shadow-sm" : "bg-white text-[#1C1924] border border-[#EEE7FA] hover:bg-[#F4F0FF]"}`}
                >
                  {occ.label}
                </button>
              ))}
            </div>

            {/* Price Buckets */}
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-0.5 whitespace-nowrap scroll-smooth md:flex-wrap">
              <span className="flex items-center gap-1 text-[11px] font-bold text-[#8b8790] uppercase tracking-wider shrink-0 pr-1">
                <SlidersHorizontal size={12} className="text-[#E5497C]" /> Price:
              </span>
              {PRICE_BUCKETS.map((b, i) => (
                <button
                  key={i}
                  data-testid={`shop-price-${i}`}
                  onClick={() => setPriceIdx(i)}
                  className={`px-3 py-1.5 rounded-full text-[12px] md:text-[12.5px] font-semibold transition shrink-0 ${priceIdx === i ? "bg-[#E5497C] text-white shadow-sm" : "bg-white text-[#1C1924] border border-[#EEE7FA] hover:bg-[#FFF4F7]"}`}
                >
                  {b.label}
                </button>
              ))}

              {/* Reset button if filters applied */}
              {(selectedOccasion !== "all" || priceIdx !== 0) && (
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-50 text-red-600 text-[12px] font-semibold hover:bg-red-100 transition shrink-0 ml-auto md:ml-0"
                >
                  <RotateCcw size={12} /> Clear
                </button>
              )}
            </div>

          </div>
        )}

        {/* Product Cards Grid */}
        {loading ? (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[...Array(8)].map((_, i) => <div key={i} className="aspect-[4/5] skeleton rounded-2xl" />)}
          </div>
        ) : filteredSorted.length === 0 ? (
          <div className="mt-16 text-center py-12 px-6 bg-white/70 rounded-3xl border border-[#F7C7DC] max-w-md mx-auto">
            <p className="font-display font-semibold text-[#1C1924] text-lg">No matching gifts found</p>
            <p className="mt-1 text-[13.5px] text-[#4A4652]">Try widening your selected occasion or price filter.</p>
            <button
              onClick={resetFilters}
              className="mt-4 px-5 py-2 rounded-full bg-[#1C1924] text-white text-[13px] font-semibold hover:bg-[#E5497C] transition"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5 md:gap-8">
            {filteredSorted.map((p) => <ProductCard key={p.id} product={p} testIdPrefix="shop-product" />)}
          </div>
        )}
      </div>
    </main>
  );
}
